import React from 'react';
import { localizeInternalHref } from '../utils/localeRouting';
import '../styles/AppErrorBoundary.css';

const ERROR_COPY = {
  en: {
    eyebrow: 'Workspace recovery',
    title: 'Stocktag hit an unexpected problem',
    body: 'Your saved local drafts were not removed. Try reopening the workspace; if the problem continues, refresh the page.',
    retry: 'Reopen workspace',
    reload: 'Refresh page',
    home: 'Return home',
    help: 'Open Help Center'
  },
  tr: {
    eyebrow: 'Çalışma alanı kurtarma',
    title: 'Stocktag beklenmeyen bir sorunla karşılaştı',
    body: 'Kaydedilmiş yerel taslaklarınız silinmedi. Çalışma alanını yeniden açmayı deneyin; sorun sürerse sayfayı yenileyin.',
    retry: 'Çalışma alanını yeniden aç',
    reload: 'Sayfayı yenile',
    home: 'Ana sayfaya dön',
    help: 'Yardım merkezini aç'
  }
};

const resolveErrorLanguage = () => {
  if (typeof document !== 'undefined') {
    const documentLanguage = String(document.documentElement.lang || '').trim();
    if (documentLanguage) return documentLanguage;
  }

  if (typeof navigator !== 'undefined') {
    const navigatorLanguage = String(navigator.language || '').trim();
    if (navigatorLanguage) return navigatorLanguage;
  }

  return 'en';
};

const resolveErrorCopy = (language) => (
  String(language || '').toLowerCase().startsWith('tr') ? ERROR_COPY.tr : ERROR_COPY.en
);

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, resetKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Stocktag application boundary caught an error.', error, errorInfo);
  }

  handleRetry = () => {
    this.setState((state) => ({
      hasError: false,
      resetKey: state.resetKey + 1
    }));
  };

  handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
    }

    const language = resolveErrorLanguage();
    const copy = resolveErrorCopy(language);

    return (
      <main className="app-error-boundary" role="alert" aria-labelledby="app-error-title">
        <section className="app-error-card">
          <div className="app-error-mark" aria-hidden="true">S</div>
          <p className="app-error-eyebrow">{copy.eyebrow}</p>
          <h1 id="app-error-title">{copy.title}</h1>
          <p className="app-error-description">{copy.body}</p>
          <div className="app-error-actions">
            <button type="button" className="app-error-primary" onClick={this.handleRetry}>
              {copy.retry}
            </button>
            <button type="button" className="app-error-secondary" onClick={this.handleReload}>
              {copy.reload}
            </button>
          </div>
          <nav className="app-error-links" aria-label={copy.eyebrow}>
            <a href="/">{copy.home}</a>
            <a href={localizeInternalHref('/help', language)}>{copy.help}</a>
          </nav>
        </section>
      </main>
    );
  }
}

export default AppErrorBoundary;
